<template>
  <div>
    <wc-page-title title='Dashboard' subtitle=''></wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12' md='3' sm='6'>
          <wc-datepicker
            label='From'
            :max='today'
            v-model='reportDateFrom'
          ></wc-datepicker>
        </v-col>
        <v-col cols='12' md='3' sm='6'>
          <wc-datepicker
            label='To'
            :max='today'
            :min='reportDateFrom'
            v-model='reportDateTo'
          ></wc-datepicker>
        </v-col>
        <v-col cols='12' md='4' class='d-flex' :class="$vuetify.breakpoint.smAndDown ? 'justify-center' : 'justify-start'">
          <v-btn
            large
            width='294px'
            :class="$vuetify.breakpoint.smAndDown ? 'mt-n3' : 'mt-1'"
            depressed
            color='primary'
            :disabled='noDate || disableGenerate'
            @click='getPayrollData()'
          >
            {{ (noDate || disableGenerate) ? 'Change date(s) for new data' : 'Generate Report' }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols='12'>
          <div>
            <span class='text-subtitle-2'>Use these buttons to filter the results</span>
            <span class='ml-2 text-caption'>This will apply automatically (no need to generate a new report)</span>
          </div>
          <template v-for='day in weekdays'>
            <v-btn
              :key='day.text'
              :width='$vuetify.breakpoint.smAndDown ? "30px" : "120px"'
              depressed
              :color="day.use ? 'info' : 'grey lighten-2'"
              class='mr-2 mt-2'
              :class="day.use ? '' : 'text-decoration-line-through text--disabled' "
              @click='day.use = !day.use'
            >
              {{ $vuetify.breakpoint.smAndDown ? day.abbreviation : day.text }}
            </v-btn>
          </template>
        </v-col>

        <v-col cols='12' v-if='fixCount > 0'>
          <v-alert
            border='top'
            colored-border
            type='error'
            class='mb-n7 pb-2 red lighten-5'
          >
            <span class='body-2 font-weight-bold pt-0 mr-6'>Please wait...</span>
            <span class='body-2 font-weight-bold pt-0'>{{ 'Repairing ' + fixCount + ' old HUB database entries' }}</span>
          </v-alert>
        </v-col>

         <v-col cols='12'>

<!-- table displaying totals  -->
          <v-data-table
            v-if='profiles.length'
            :loading='isLoading'
            dense
            :headers='totalHeaders'
            :items='totals'
            class='grey lighten-3'
            hide-default-footer
            disable-pagination
            disable-sort
          >
            <template v-slot:item.name_display>
              <v-btn x-small depressed color='grey lighten-1' @click='hideInactive = !hideInactive'>
                {{ hideInactive ? "Show " : "Hide " }} Inactive Employees
              </v-btn>
            </template>

            <template v-slot:item.total_hours='{ item }'>
              <span class='font-weight-bold'> {{ item.total_hours }}</span>
            </template>

            <template v-slot:item.kms='{ item }'>
              <span class='font-weight-bold'> {{ item.kms }}</span>
            </template>

          </v-data-table>

<!-- main table showing all employees -->
          <v-data-table
            v-if='profiles.length'
            dense
            :headers='headers'
            :items='filteredProfiles'
            :expanded.sync='expanded'
            item-key='id'
            show-expand
            no-data-text='No Profiles Found'
            hide-default-footer
            disable-pagination
          >

            <template v-slot:item.name_display='{ item }'>
              <span :class='getNameClass(item)'>{{ item.name_display }}</span>
              <v-icon v-if='item.high_season_help' class='ml-1' color='info' small>mdi-account-hard-hat</v-icon>
            </template>

            <template v-slot:item.alerts='{ item }'>
              <template v-if='getNameClass(item) === "purple--text"'>
                <span class='purple--text'>Details Missing</span>
                <v-icon right small color='purple'>mdi-alert</v-icon>
              </template>
              <template v-if='getNameClass(item) === "error--text"'>
                <span class='error--text'>Duplicate Entry Found!</span>
                <v-icon right small color='error'>mdi-alert-octagon</v-icon>
              </template>
            </template>

            <template v-slot:item.total_hours='{ item }'>
              <span v-if='item.total_hours' class='font-weight-bold'> {{ item.total_hours }}</span>
            </template>

            <template v-slot:item.kms='{ item }'>
              <span> {{ item.kms }}</span>
            </template>
            
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan='headers.length' class='pr-0 py-1 pl-12'>
                <payroll-entry-table
                  :entries='getEntries(item.id)'
                  @openEntry='openEntry'
                  @addEntry='addEntry'
                  @deleteEntry='confirmDeleteEntry'
                ></payroll-entry-table>
              </td>
            </template>

          </v-data-table>
        </v-col>
      </v-row>

      <v-btn
        v-if='madeEdits'
        fab
        fixed
        bottom
        right
        large
        color='green'
        class='bounce'
        @click='refreshData'
      >
        <v-icon color='white'>mdi-refresh</v-icon>
      </v-btn>

    </v-container>

    <entry-basics-dialog
      :showDialog='showDialog'
      :entry='entryCopy'
      :profiles='profiles'
      payrollEntry
      @cancel='showDialog = false'
      @openEntry='openEntry'
      @returnToDashboard='showDialog = false'
    ></entry-basics-dialog>

    <wc-confirm :showDialog='showConfirmDeleteEntry' :loading='isLoading' @confirm='deleteEntry' @cancel='showConfirmDeleteEntry = false'>
      Are you sure you want to delete this entry?<br>Any connected details will be lost.<br><br> <b>This cannot be undone!</b>
    </wc-confirm>

  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import timesheetServices from '@/modules/timesheets/services/timesheets'
import payrollAPI from '@/modules/payroll/api/payroll'
import staffingAPI from '@/modules/staffing/api/staffing'
import timesheetsAPI from '@/modules/timesheets/api/timesheets'
import PayrollEntryTable from '../components/PayrollEntryTable'
import EntryBasicsDialog from '@/modules/timesheets/components/EntryBasicsDialog'

export default {
  name: 'payroll-dashboard',
  data: () => ({
    isLoading: true,
    showDialog: false,
    fixCount: 0,
    showConfirmDeleteEntry: false,
    reportDateFrom: null,
    reportDateTo: null,
    disableGenerate: false, // this is for improved user feedback (so they know when they don't need to re-generate data)
    disableDateClear: false, // this is to prevent a page crash when clear is used after generate has happened (bug)
    reportGenerated: false,
    hideInactive: false,
    madeEdits: false,
    entryCopy: { name_display:"" },
    expanded: [],
    panel: [],
    profiles: [],
    payrollData: [],
    tableData: [],
    days: [],
    entries: [],
    headers: [
      { text: 'Employee', value: 'name_display', class:'overline', align: 'start', width: '348' },
      { text: '', value: 'alerts', class:'overline', align: 'end', sortable: false },
      { text: 'Hours', value: 'total_hours', class:'overline', align: 'end', width: '100' },
      { text: 'Kms', value: 'kms', class:'overline', align: 'end', width: '100' },
      { text:'', value: 'data-table-expand' }
    ],
    totalHeaders: [
      { text: '', value: 'name_display', class:'overline white', align: 'start' },
      { text: '', value: 'alerts', class:'overline white', align: 'end', width: '200'},
      { text: '', value: 'total_hours', class:'overline white', align: 'end', width: '100' },
      { text: '', value: 'kms', class:'overline white', align: 'end', width: '100' },
      { text:'', value: 'blank_space', class: 'white', width: '56' }
    ],
    weekdays: [
      { text: 'Sunday', abbreviation: 'Sun', use: true },
      { text: 'Monday', abbreviation: 'Mon', use: true },
      { text: 'Tuesday', abbreviation: 'Tue', use: true },
      { text: 'Wednesday', abbreviation: 'Wed', use: true },
      { text: 'Thursday', abbreviation: 'Thu', use: true },
      { text: 'Friday', abbreviation: 'Fri', use: true },
      { text: 'Saturday', abbreviation: 'Sat', use: true }
    ]
    
  }),
  mounted () {
    this.reportDateFrom = this.monthAgo
    this.reportDateTo = this.today
    this.getProfiles()
    this.isLoading = false
  },
  computed: {
    today () {
      return moment().format('YYYY-MM-DD').toString()
    },
    monthAgo () {
      return moment().subtract(1, 'months').format('YYYY-MM-DD').toString()
    },
    noDate () {
      if (!this.reportDateFrom || !this.reportDateTo) {
        return true
      }
      return false
    },
    totals () {
      return [{
        alerts: 'Report Totals:',
        total_hours: this.getTotalReportHours(),
        kms: this.getTotalReportKilometers() }]
    },
    activeDays () {
      let dayArray = []
      let dayObjects = _.filter(this.weekdays, ['use', true])
      dayObjects.forEach(p => dayArray.push(p.text))
      return dayArray
    },
    filteredPayrollData () {
      return _.filter(this.payrollData, pd => _.includes(this.activeDays, moment(pd.date).format('dddd').toString() ) )
    },
    filteredProfiles () {
      if (this.hideInactive) {
        return _.filter(this.profiles, ['is_active', true])
      }
      return this.profiles
    },
  },
  watch: {
    'activeDays.length' () {
      this.getAllHourTotals()
      this.getAllKilometerTotals()
    },
    reportDateFrom () {
      this.disableGenerate = false
    },
    reportDateTo () {
      this.disableGenerate = false
    }
  },
  methods: {
    async getProfiles () {
      this.profiles = await staffingAPI.getProfiles()
    },
    async getPayrollData() {
      this.isLoading = true
      this.payrollData = await payrollAPI.getPayrollReportData (this.reportDateFrom, this.reportDateTo)
      this.fixOldData()
      this.getAllHourTotals()
      this.getAllKilometerTotals()
      this.disableGenerate = true
      this.disableDateClear = true
      this.reportGenerated = true
      this.isLoading = false
    },
    fixOldData() {
      let tmp = _.filter(this.payrollData, p => p.hours_left_to_assign === null || p.kms === null)
      console.log('need to fix ' + tmp.length + ' entries')
      if (tmp.length) {
        this.fixCount = tmp.length
        tmp.forEach(t => {
          this.fixNullValuesEntry(t)
        })
      }
    },
    async fixNullValuesEntry(entry) {
      let entryCopy = _.cloneDeep(entry)
      if (entryCopy.hours_left_to_assign === null) {
        entryCopy.hours_left_to_assign = await timesheetServices.updateEntryHoursLeft(entry)
      }
      if (entryCopy.kms === null) {
        entryCopy.kms = 0
      }
      await timesheetsAPI.updateTimesheetEntry(entryCopy)
      this.fixCount -= 1
    },
    getColor(entry) {
      if (!entry.id) {
        return 'amber darken-3'
      } else if (entry.hours_left_to_assign !== 0) {
        return 'purple'
      } else if (entry.start === entry.end) {
        return 'grey'
      }
      return 'info'
    },
    getDay(date){
      return moment(date).format('dddd').toString()
    },
    openEntry (entry) {
      this.madeEdits = true
      console.log(entry)
      const link = document.createElement('a')
      const invURL = this.$router.resolve({ name: 'TimesheetEntry', params: { id: entry.id } })
      link.target = '_blank'
      link.href = invURL.href
      link.click()
      this.showDialog = false
    },
    addEntry(entry) {
      this.entryCopy = _.cloneDeep(entry)
      let user = _.find(this.profiles, function(p) { return p.id === entry.employee })
      this.entryCopy.start = user.default_start
      this.entryCopy.end = user.default_end
      this.entryCopy.had_lunch = false
      this.showDialog = true
    },
    confirmDeleteEntry(entry) {
      this.entryCopy = _.cloneDeep(entry)
      this.showConfirmDeleteEntry = true
    },
    async deleteEntry() {
      this.isLoading = true
      this.entryCopy = await timesheetsAPI.deleteTimesheetEntry(this.entryCopy.id)
      await this.getPayrollData()
      this.showConfirmDeleteEntry = false
      this.isLoading = false
    },
    refreshData() {
      this.madeEdits = false
      this.getPayrollData()
    },
    totaledEmployeeHours (profileID) {
      let entries = _.filter(this.filteredPayrollData, ['employee', profileID])
      return _.sumBy(entries, 'hours_worked') !== null ? _.sumBy(entries, 'hours_worked').toFixed(2) : 0
    },
    totaledEmployeeKilometers (profileID) {
      let entries = _.filter(this.filteredPayrollData, ['employee', profileID])
      return _.sumBy(entries, 'kms') !== null ? _.sumBy(entries, 'kms').toFixed(2) : 0
    },
    getEntries(profileID) {
      if (this.reportGenerated) {
        let entries = _.filter(this.filteredPayrollData, ['employee', profileID])
        entries = _.orderBy(entries, 'date', 'asc')
        for ( let i=1; i < entries.length; i++ ) {
          if (entries[i].date === entries[i-1].date) {
            entries[i].duplicateDate = true
            entries[i-1].duplicateDate = true
          }
        }
        let date = this.reportDateFrom
        let endDate = moment(this.reportDateTo).add(1, 'days').format('YYYY-MM-DD').toString()
        do {
          if (!_.some(entries, {'date': date})) {
            entries.push({'date': date, 'employee': profileID, 'hours_worked': 0, 'kms': 0 })
          }
          date = moment(date).add(1, 'days').format('YYYY-MM-DD').toString()
        }
        while (date !== endDate)
        return _.orderBy(entries, 'date', 'desc')
      }
      return []
    },
    getTotalReportHours () {
      return _.sumBy(this.filteredPayrollData, 'hours_worked').toFixed(2)
    },
    getTotalReportKilometers () {
      return _.sumBy(this.filteredPayrollData, 'kms').toFixed(2)
    },
    getAllHourTotals () {
      this.filteredProfiles.forEach(p => p.total_hours = this.totaledEmployeeHours(p.id))
    },
    getAllKilometerTotals () {
      this.filteredProfiles.forEach(p => p.kms = this.totaledEmployeeKilometers(p.id))
    },
    getNameClass (profile) {
      let entries = _.filter(this.filteredPayrollData, ['employee', profile.id])
      let unassigned = _.sumBy(entries, 'hours_left_to_assign')
      if (!profile.is_active) {
        return 'text-decoration-line-through text--secondary'
      } else if ( _.uniqBy(entries, 'date').length !== entries.length ) {
        return 'error--text'
      } else if (unassigned > 0) {
        return 'purple--text'
      }
      return 'text--primary'
    },
  },
  components: {
    'payroll-entry-table': PayrollEntryTable,
    'entry-basics-dialog': EntryBasicsDialog
  }
}
</script>

<style scoped>

.bounce { animation: bounce 2s linear 2s infinite forwards; }

@keyframes bounce {
    0% { transform: scale(1.0); }
   20% { transform: rotate(20deg) scale(1.4); }
   40% { transform: rotate( 0deg) scale(1.0); }
  100% { transform: scale(1.0); }
}

</style>
